import {Button} from '@/components/atoms'
import {UploadFile} from '@/components/molecules'
import {Constants, NUMBERS, Size, Variant} from '@/utils/constants/commonConstants'

/**
 * FileUploadWithoutModal component renders a file upload interface with optional download template and view table buttons.
 *
 * @param {Object} props - The properties object.
 * @param {string} [props.headerTitle=''] - The title to display in the header.
 * @param {Function} props.handleDownloadTemplate - Function to handle the download template button click.
 * @param {string} [props.viewTableButtonText=Constants.ViewDataTable] - Text for the view table button.
 * @param {Function} props.handleViewTable - Function to handle the view table button click.
 * @param {string} [props.label=''] - Label for the file upload input.
 * @param {string} [props.description=''] - Description for the file upload input.
 * @param {Array} props.acceptedExtensions - Array of accepted file extensions.
 * @param {Array} props.files - Array of files to be uploaded.
 * @param {Function} props.onUpload - Function to handle file upload.
 * @param {string} props.feedbackType - Type of feedback to display.
 * @param {Function} [props.onUploadDelete=() => {}] - Function to handle file deletion.
 * @param {string} [props.helperText=''] - Helper text to display.
 * @param {Function} [props.onUploadError=() => {}] - Function to handle upload errors.
 * @param {string} [props.className=''] - Additional class names for the component.
 * @param {string} [props.containerClassName=''] - Additional class names for the container.
 * @param {string} props.variant - Variant of the upload input.
 * @param {string} props.type - Type of the upload input.
 * @param {boolean} [props.showDownloadTemplate=true] - Flag to show or hide the download template button.
 * @param {boolean} [props.showHandleViewTable=true] - Flag to show or hide the view table button.
 * @param {boolean} [props.disabled=false] - Flag to disable the upload input.
 *
 * @returns {JSX.Element} The rendered FileUploadWithoutModal component.
 */

export default function FileUploadWithoutModal({
  headerTitle = '',
  handleDownloadTemplate,
  viewTableButtonText = Constants.ViewDataTable,
  handleViewTable,
  label = '',
  description = '',
  acceptedExtensions,
  files,
  onUpload,
  feedbackType,
  onUploadDelete = () => {},
  helperText = '',
  onUploadError = () => {},
  className = '',
  containerClassName = '',
  variant,
  type,
  showDownloadTemplate = true,
  showHandleViewTable = true,
  disabled = false,
} = {}) {
  return (
    <>
      {showDownloadTemplate && (
        <div className="mb-6 flex items-center justify-between">
          <h1 className="max-w-[70%] text-2xl font-bold leading-8">{headerTitle}</h1>
          <Button
            handleButton={handleDownloadTemplate}
            uiSchema={{
              title: Constants.DownloadTemplate,
              size: Size.Medium,
              variant: Variant.Secondary,
            }}
          />
        </div>
      )}
      <UploadFile
        isDark={true}
        acceptedExtensions={acceptedExtensions}
        description={description}
        label={label}
        variant={variant}
        required={true}
        files={files}
        onUpload={onUpload}
        feedbackType={feedbackType}
        onUploadDelete={onUploadDelete}
        helperText={helperText}
        onUploadError={onUploadError}
        type={type}
        className={className}
        containerClassName={containerClassName}
        dataTestId="upload-file"
        disabled={disabled}
      />
      {showHandleViewTable && files?.length > NUMBERS.Zero && (
        <div className="mb-6 flex flex-wrap py-2">
          <Button
            handleButton={handleViewTable}
            uiSchema={{
              title: viewTableButtonText,
              size: Size.Medium,
              variant: Variant.Primary,
            }}
          />
        </div>
      )}
    </>
  )
}
